import React from 'react';
import { UseFormRegister } from 'react-hook-form';
import * as yup from 'yup';

import Button from '@/core/components/Button';
import Icons from '@/core/components/Icons';
import { ToastStatus } from '@/core/components/Toast/Toast';
import useForm from '@/core/hooks/useForm';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import { useNativeContext } from '@/core/lib/native/native.context';
import { useAuthContext } from '@/core/lib/new-architecture/context/auth.context';
import Query from '@/core/lib/new-architecture/query';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { UsersHasControlResult } from '@/core/types/control';
import { interests } from '@/core/types/interests';

const schema = yup.object({
  interests: yup.array(yup.string().oneOf(interests).required()),
});

type Schema = yup.InferType<typeof schema>;

interface Data {
  interests?: string[];
}

export const InterestsForm: React.FC<{ onCancel: () => void; onSubmit: () => Promise<void>; content: string; cancelButtonText: string }> = ({
  onCancel,
  onSubmit: onSubmitProps,
  content,
  cancelButtonText,
}) => {
  const { t } = useTranslationContext(['common']);
  const { DONNONS_API_URL } = useEnvContext();
  const { accessToken } = useAuthContext();
  const toast = useToastContext();

  const { data: interestsResult } = Query.interests.useInterests({ token: accessToken });
  const interestsInvalidate = Query.interests.useInvalidate();
  const savedSearch = Query.savedSearch.useInvalidate();
  const { isNative } = useNativeContext();

  const { register, onSubmit } = useForm<UsersHasControlResult, Data, Schema>({
    schema,
    apiDefinition: { method: ApiMethods.PUT, url: `${DONNONS_API_URL}${ApiVersions.V1}/user/interests` },
    onSuccess: async () => {
      await savedSearch.invalidate();
      await interestsInvalidate.invalidate();
      await onSubmitProps();
      toast?.open(t(`thematiques.update-success`, { ns: 'common' }), 3000, ToastStatus.SUCCESS);
    },
    values: { interests: interestsResult?.interests },
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex w-full grow flex-col gap-6">
        <p className="text-body-primary text-center font-semibold text-content-secondary">{t('control.interests.title', { ns: 'common' })}</p>
        <p className="text-body-secondary whitespace-break-spaces font-semibold text-content-primary">{content}</p>
        <form>
          <div className="grid gap-3 lg:grid-cols-3">
            {interests.map(interest => (
              <Interest key={interest} interestName={interest} register={register} />
            ))}
          </div>
        </form>
      </div>
      <div className={`flex gap-4 ${isNative ? 'mb-14' : ''}`}>
        <Button variant="secondary" onClick={onCancel}>
          {cancelButtonText}
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          {t('control.interests.submit', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
};

const Interest: React.FC<{
  interestName: string;
  register: UseFormRegister<Schema>;
}> = ({ interestName, register }) => {
  const { t } = useTranslationContext(['common']);
  const isLg = useIsLg();

  const content = t(`thematiques.${interestName}.title`, { ns: 'common' });
  return (
    <label
      className="text-body-primary group cursor-pointer overflow-hidden rounded-2 bg-cover bg-center text-center font-semibold text-content-button-primary lg:min-h-[130px]"
      style={{ backgroundImage: `url(/assets/interests/${interestName}-${isLg ? 'lg' : 'sm'}.jpg)` }}
    >
      <span className="relative grid h-full min-h-16 p-5 max-lg:grid-cols-[24px_1fr_24px] lg:grid-rows-[32px_1fr_32px]">
        <input type="checkbox" value={interestName} {...register('interests')} className="peer hidden" />
        <span className="absolute right-0 top-0 size-full bg-content-primary/30 peer-checked:bg-content-primary/60 lg:group-hover:bg-content-primary/60" />

        <span />
        <span className="z-10 self-center">{content}</span>
        <span className="z-10 hidden items-center justify-center rounded-1 bg-content-secondary peer-checked:grid lg:size-6 lg:place-self-end">
          <Icons icon="check" color="button-primary" />
        </span>
      </span>
    </label>
  );
};
